import { isChinaUser } from 'utils/ChinaUtil';
import { checkIsUtagReady, UtagAnalyticsType, initializeGA4, sendAnalyticsPayload } from '@garmin/analytics';
import { initializeMatomo, pushEventToMatomo, setCustomDimensions } from './Matomo';
import { getCookie, setCookie } from 'utils/CookieUtil';

const GA_CHECK_DELAY = 5000; //5s in ms
const MAX_GA_CHECK_COUNT = 3; // how many attempts to push too early events
const utagReadyEvent = new Event('utag-ready');

let checkGA3Count = -1;
let checkGA4Count = -1;
/**
 * Todo: remove this when all Tealium Removal changes are done
 * isTealiumDisabled:
 *  - true: setup GA and Matomo WITHOUT Tealium (utag.js)
 *  - false: setup GA and Matomo WITH Tealium (utag.js)
 */
let isTealiumDisabled = false;
let isGAAndMatomoInitialized = false;
let isFunctionalCookiesGranted = false; // this would be flipped when user updates the Functional Cookies pref through TrustArc banner
const isProd = window.location.host === 'connect.garmin.com';

/**
 * Check if Matomo is enabled or not
 * @description
 *   We only have one Matomo space, so we should not enable other env to pollute the user data.
 *   However, it could be enabled temporarily in localhost or VM, for debug or testing purpose
 * @returns {boolean}
 */
const isMatomoEnabled = () => {
    return isProd && isFunctionalCookiesGranted;
};

/** Deprecated logic to be removed in the near future */
function setTimeoutGA3Push() {
    if (checkGA3Count < MAX_GA_CHECK_COUNT) {
        console.log(`Checking if GA3 is setup: ${checkGA3Count < MAX_GA_CHECK_COUNT}`);
        checkGA3Count++;
        setTimeout(() => {
            delayedGA3Push();
        }, GA_CHECK_DELAY);
    }
}

/** Deprecated logic to be removed in the near future */
function delayedGA3Push() {
    // Check if GA is loaded
    if (window.ga) {
        checkGA3Count = MAX_GA_CHECK_COUNT;
        // Once loaded, loop through all events that were fired before GA loaded.
        while (window._ga_universal.length) {
            // This line is weird...
            // utag overrides the push method, so it doesn't push into the array any more, it pushes to google analytics
            // this will make shift eventually empty any events that were pushed into the array before utag loaded
            window._ga_universal.push(window._ga_universal.shift(1));
        }

        return;
    }

    // Recursion break after too many attempts checking for GA loaded.
    setTimeoutGA3Push();
}

/** Deprecated logic to be removed in the near future */
function setTimeoutGA4Push() {
    if (checkGA4Count < MAX_GA_CHECK_COUNT) {
        console.log(`Checking if GA4 is setup: ${checkGA4Count < MAX_GA_CHECK_COUNT}`);
        checkGA4Count++;
        setTimeout(() => {
            delayedGA4Push();
        }, GA_CHECK_DELAY);
    }
}

/** Deprecated logic to be removed in the near future */
function delayedGA4Push() {
    // Check if GA is loaded
    if (checkIsUtagReady()) {
        checkGA4Count = MAX_GA_CHECK_COUNT;
        window.dispatchEvent(utagReadyEvent);

        return;
    }

    // Recursion break after too many attempts checking for GA loaded.
    setTimeoutGA4Push();
}

/**
 * Initialize GA3/GA4 with Tealium
 * Deprecated logic to be removed in the near future
 */
const initialzeGAWithTealium = () => {
    //It adds an event listener on 'utag-ready' to process the events in queue
    initializeGA4();

    // Check to see if utag has loaded, if not, start a poll to push events once it's loaded
    setTimeoutGA3Push();
    setTimeoutGA4Push();
};

/** Deprecated logic to be removed in the near future */
const addUtagReadyEvent = () => {
    window.addEventListener('utag-ready', () => {
        console.log('GA4 event queue processing should start!');
    });
};

/**
 * When the Functional Cookies preference is updated by user through TrustArc Widget
 *   1. On Granted, initialize GA4 and Matomo if it's not done yet (due to required consent not given on page load)
 *   2. For Matomo, push consent event based on granted or not
 *   3. For Google Tag (GA4), update consent value of 'analytics_storage' based on granted or not
 *   4. Update the flag 'isFunctionalCookiesGranted', which is mainly for Matomo to control the event firing
 * @param {boolean} granted
 */
const handleFunctionalCookiesPrefChange = (granted) => {
    if (isChinaUser()) return;

    if (granted && !isGAAndMatomoInitialized) {
        initializeGAAndMatomo(isTealiumDisabled, true);
    }
    pushEventToMatomo(granted ? 'setCookieConsentGiven' : 'requireCookieConsent');
    updateConsentAnalyticsStorage(granted);
    isFunctionalCookiesGranted = granted;
};

/**
 * Event Handler for Message events
 * @param {*} e
 */
const handleMessageEvent = (e) => {
    if (typeof e?.data === 'string') {
        const dataJson = JSON.parse(e.data);
        if (dataJson?.message === 'submit_preferences' && !!dataJson.data) {
            /**
             * '0': Required Cookies: This can't be toggled, would be always returned anyway
             * '1': Functional Cookies: This can be togged on/off
             * '2': Advertising Cookies: This can be togged on/off
             */
            const consentPreferences = typeof dataJson.data === 'object' ? dataJson.data.value : dataJson.data;

            let newCookieValue = '';
            let consent = false;
            const timestamp = Date.now();
            switch (consentPreferences) {
                case '0,1,2':
                    // Accept All, set c1 - c15 all to '1'
                    consent = true;
                    newCookieValue = `c1:1%7Cc2:1%7Cc3:1%7Cc4:1%7Cc5:1%7Cc6:1%7Cc7:1%7Cc8:1%7Cc9:1%7Cc10:1%7Cc11:1%7Cc12:1%7Cc13:1%7Cc14:1%7Cc15:1%7Cts:${timestamp}%7Cconsent:${consent}`;
                    handleFunctionalCookiesPrefChange(true);
                    break;
                case '0':
                    // Decline All, set c1 - c15 all to '0'
                    newCookieValue = `c1:0%7Cc2:0%7Cc3:0%7Cc4:0%7Cc5:0%7Cc6:0%7Cc7:0%7Cc8:0%7Cc9:0%7Cc10:0%7Cc11:0%7Cc12:0%7Cc13:0%7Cc14:0%7Cc15:0%7Cts:${timestamp}%7Cconsent:${consent}`;
                    handleFunctionalCookiesPrefChange(false);
                    break;
                case '0,1':
                    // Only check the Functional Cookies, set c3 to '0', all others to '1'
                    consent = true;
                    newCookieValue = `c1:1%7Cc2:1%7Cc3:0%7Cc4:1%7Cc5:1%7Cc6:1%7Cc7:1%7Cc8:1%7Cc9:1%7Cc10:1%7Cc11:1%7Cc12:1%7Cc13:1%7Cc14:1%7Cc15:1%7Cts:${timestamp}%7Cconsent:${consent}`;
                    handleFunctionalCookiesPrefChange(true);
                    break;
                case '0,2':
                    // Only check the Advertising Cookies, set c3 to '1', all others to '0'
                    newCookieValue = `c1:0%7Cc2:0%7Cc3:1%7Cc4:0%7Cc5:0%7Cc6:0%7Cc7:0%7Cc8:0%7Cc9:0%7Cc10:0%7Cc11:0%7Cc12:0%7Cc13:0%7Cc14:0%7Cc15:0%7Cts:${timestamp}%7Cconsent:${consent}`;
                    handleFunctionalCookiesPrefChange(false);
                    break;
                default:
                    break;
            }

            if (newCookieValue) {
                const cookieKey = 'CONSENTMGR';
                const cookieOptions = { raw: true, domain: '.garmin.com', path: '/' };
                // update cookie value
                setCookie(cookieKey, newCookieValue, cookieOptions);
                // update GDPR consent to Matomo
                if (isMatomoEnabled()) {
                    setCustomDimensions();
                }
            }
        }
    }
};

/**
 * Add event listener for preference submit from Trustarc consent widget
 */
export const addConsentPreferenceSubmitEventListener = () => {
    window.addEventListener('message', handleMessageEvent);
};

/**
 * Remove event listener for preference submit from Trustarc consent widget
 */
const removeConsentPreferenceSubmitEventListener = () => {
    window.removeEventListener('message', handleMessageEvent);
};

/**
 * Follow the below guide to install and configure Google Tag for PROD & TEST
 * https://support.google.com/tagmanager/answer/12002338?sjid=10182747102766321404-AP#zippy=%2Cset-up-your-google-tag-in-campaign-manager-instructions%2Cexample-google-tag
 */
const loadGoogleTagScript = () => {
    const tagId = isProd ? 'G-DHVMQZ6WGH' : 'G-NDX9XMV4PZ'; // the IDs are from Google Tag Manager
    /** inject script to load gtag/js file */
    const sourceScript = document.createElement('script');
    sourceScript.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
    sourceScript.async = true;
    sourceScript.type = 'text/javascript';
    // push script to HTML header
    document.head.appendChild(sourceScript);

    /**
     * inject script to configure google tag
     *   1. configure default values for user consents
     *     a. Deny all ad related consents
     *     b. Grant 'analytics_storage' for page_view events tracking purpose,
     *        because the script would be loaded only when consent is given (for EU) or not required,
     *        so it's safe to grant it by default
     *   2. configure the Tag ID based on which env user is in
     */
    const configScript = document.createElement('script');
    const configScriptContent = `
        <!-- Google tag (gtag.js) -->
        window.dataLayer = window.dataLayer || [];
        function gtag() {
            dataLayer.push(arguments);
        }
        gtag('consent', 'default', {
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied',
            'analytics_storage': 'granted',
        });
        gtag('js', new Date());
        gtag('config', '${tagId}');
    `;
    configScript.textContent = configScriptContent;
    // push script to HTML header
    document.head.appendChild(configScript);
};

/**
 * check if required consent is given, especially for EU regions
 * @returns
 */
const isConsentGiven = () => {
    const cookieKeyTrackerBehavior = 'notice_behavior';
    const cookieKeyGdprPref = 'notice_gdpr_prefs';
    const trackerBehavior = getCookie(cookieKeyTrackerBehavior) ?? '';
    const gdprPref = getCookie(cookieKeyGdprPref) ?? '';

    let consentGiven = false;
    const trackerBehaviorOptions = ['implied,eu', 'implied,us', 'expressed,eu', 'expressed,us'];
    if (trackerBehaviorOptions.includes(trackerBehavior)) {
        // '1' represents the Functional coockies which include google analytics domains
        consentGiven = !!gdprPref
            .split(':')
            .at(0)
            .split(',')
            .includes('1');
    } else if (trackerBehavior === 'none') {
        // consent is not required for the user region
        consentGiven = true;
    }
    return consentGiven;
};

/**
 * Update the Analytics Storage Consent of Google Tag
 * @param {boolean} granted
 */
const updateConsentAnalyticsStorage = (granted) => {
    window.dataLayer = window.dataLayer || [];
    function gtag() {
        dataLayer.push(arguments);
    }
    gtag('consent', 'update', {
        analytics_storage: granted ? 'granted' : 'denied'
    });
};

/**
 * Load Tealium Script (utag.js)
 * Deprecated logic to be removed in the near future
 */
const loadTealiumScript = () => {
    const script = document.createElement('script');
    script.src = 'https://tags.tiqcdn.com/utag/garmin/main/prod/utag.js';
    script.async = true;
    script.type = 'text/javascript';
    // copying timeout logic provided from Digital Insights team
    setTimeout(function() {
        document.head.appendChild(script);
    }, 500);
};

/**
 * Todo: removed the feature flag check when the Tealium Removal tasks are all done
 */
export const initializeGAAndMatomo = (disabled, consentGranted = false) => {
    if (isChinaUser()) return;

    isTealiumDisabled = disabled;

    if (isTealiumDisabled) {
        /** if the consent is required and not given yet, just don't load GA4 and Matomo */
        if (!consentGranted && !isConsentGiven()) return;

        isFunctionalCookiesGranted = true;
        loadGoogleTagScript();
        if (isMatomoEnabled()) {
            initializeMatomo();
        }
        isGAAndMatomoInitialized = true;
    } else {
        /** Deprecated logic to be removed in the near future */
        removeConsentPreferenceSubmitEventListener(); // Tealium has added the same event listener, so remove the duplicated one
        loadTealiumScript();
        initialzeGAWithTealium();
        addUtagReadyEvent();
    }
};

export default {
    /**
     *
     * Fields that can be used for analytics:
     *
     * event - event type, usually track_event or track_pageview
     * category - the top level category, ex: Gear Page Events
     * action - the action that was performed - ex: switch tab or click activity link
     * label (optional) - additional information for the action that occured - ex: Tab Name
     *
     * Note: 'value' used to be a supported field but is no longer supported.
     *
     */
    push: function(obj) {
        /** Don't track events for China */
        if (isChinaUser()) return;

        if (!obj.event || !obj.category || !obj.action) {
            throw 'Invalid event, category, or action in Google Analytics push.';
        } else {
            if (isTealiumDisabled) {
                if (isMatomoEnabled()) {
                    // push events to Matomo
                    if (obj.event === 'track_event') {
                        const matomoFields = [obj.category, obj.action];
                        if (typeof obj.label === 'string' && obj.label.length > 0) {
                            matomoFields.push(obj.label);
                        }
                        if (document.referrer) {
                            pushEventToMatomo('setReferrerUrl', [document.referrer]);
                        }
                        pushEventToMatomo('trackEvent', matomoFields);
                    } else if (obj.event === 'track_pageview') {
                        /**
                         * Matomo reference: https://matomo.org/faq/how-to/how-do-i-set-a-custom-url-using-the-matomo-javascript-tracker/
                         * Important note: The Custom URL must be set before a Pageview tracking request is sent using trackPageView
                         */
                        pushEventToMatomo('setCustomUrl', [window.location.href]);
                        pushEventToMatomo('trackPageView');
                    }
                } else if (!isProd) {
                    /**
                     * Add console logs for non-Prod env, so Web Dev & QA can easily validate what would be sent to Matomo in PROD
                     *
                     * Usage:
                     *   To enable the debug console log, add 'enableMatomoLogs' to cookie and set it to 'true'.
                     */
                    const enableMatomoLogs = getCookie('enableMatomoLogs') === 'true';
                    if (!enableMatomoLogs) return;

                    const isCustomEvent = obj.event === 'track_event';
                    const eventType = isCustomEvent ? 'Custom Event' : 'Page View';
                    const eventPayload = isCustomEvent
                        ? { Category: obj.category, Action: obj.action }
                        : { customUrl: window.location.href };
                    if (isCustomEvent && obj.label) {
                        // in Matomo, the label would be stored as 'Name'
                        eventPayload.Name = obj.label;
                    }
                    console.log(`[Analytics Log] Would push "${eventType}" to Matomo in PROD.`, eventPayload);
                }
            } else {
                /** Deprecated logic to be removed in the near future */
                const fields = [obj.event, obj.category, obj.action];
                if (typeof obj.label !== 'undefined' && obj.label !== null) {
                    fields.push(obj.label);
                }

                window._ga_universal = window._ga_universal || [];
                window._ga_universal.push(fields);
                let tealiumEvent = {
                    tealium_event: 'gc_unlabelled_event',
                    page_name: obj.pageName ?? 'Garmin Connect',
                    event_category: obj.category,
                    event_action: obj.action
                };

                if (obj.label) {
                    tealiumEvent.tealium_event = 'gc_labelled_event';
                    tealiumEvent.event_label = obj.label;
                }

                sendAnalyticsPayload(tealiumEvent, UtagAnalyticsType.LINK);
            }
        }
    }
};
